import React, { useMemo, useRef, useState } from "react";
import { Grid, AgGrid, Button, FileIcon } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import {
  type AttrStates,
  attrCellFormatter,
  attrCellTooltip,
  dashFormatter,
  rowAttrStates,
} from "./attributeCell";
import { attributeFilterDef } from "./attributeFilters";
import {
  allScopeGlobalColumns,
  spaceHasStatusColumn,
  spaceScopeGlobalColumns,
  withAttributeMeta,
} from "./constants";
import { NEEDS_REVIEW, REVIEW_ROW_STYLE, StatusCell } from "./statusCell";
import * as styles from "./styles_vanilla.css";
import type {
  VaultDocument,
  QuarantineItem,
  Space,
  VaultDomain,
  GlobalAttribute,
} from "#common/types/vault";
import type { AttributeColumnSpec } from "./constants";
import type { DocumentBrowserProps, DocumentBrowserTab } from "./types";

export type { DocumentBrowserProps, DocumentBrowserTab };

/** A flattened grid row. Fixed presentation fields plus dynamic attribute columns. */
type Row = {
  _id: string;
  _file: string;
  _domId: string | null;
  _review: boolean;
  _problem: string | null;
  _ts: number;
  /** Tri-state per attribute COLUMN key (DV-504) — see `attributeCell.ts`. */
  _attrStates?: AttrStates;
  name: string;
  space: string | null;
  domain: string | null;
  status: string;
  modified: string;
  [key: string]: unknown;
};

/** A grid column backed by an attribute, plus the metadata that picks its filter. */
type ColumnSpec = AttributeColumnSpec;

const T2_CELL_STYLE = { background: "rgba(225, 239, 240, 0.4)" };

/* ---------------- Cell renderers ---------------- */
function NameCell({ data, value }: { data?: Row; value?: unknown }) {
  if (!data) return null;
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- value is an opaque grid cell value, defensively stringified for display
  const label = value == null || value === "" ? "—" : `${String(value)}.${data._file || "doc"}`;
  return (
    <span
      className={data._review ? `${styles.nameCell} ${styles.nameReview}` : styles.nameCell}
    >
      <FileIcon extension={data._file} size="medium" />
      <span>{label}</span>
    </span>
  );
}

function DomainCell({ data }: { data?: Row }) {
  if (!data || !data.domain) return <span className={styles.dash}>—</span>;
  return <>{data.domain}</>;
}

function ModifiedCell({ value }: { value?: unknown }) {
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- value is an opaque grid cell value, defensively stringified for display
  return <span className={styles.monoCell}>{value ? String(value) : "—"}</span>;
}

/* ---------------- Scope → column contract ---------------- */
type Scope = {
  showSpace: boolean;
  hasStatus: boolean;
  globalCols: ColumnSpec[];
  localCols: ColumnSpec[];
};

function resolveScope(spaceObj: Space | null, globalLib: GlobalAttribute[]): Scope {
  if (!spaceObj) {
    return {
      showSpace: true,
      hasStatus: true,
      // Carry each facet's catalog data type through so `buildColumns` can pick
      // a filter control for it.
      globalCols: withAttributeMeta(allScopeGlobalColumns(globalLib), globalLib),
      localCols: [],
    };
  }
  const localCols = spaceObj.locals.map((a) => ({
    key: `loc:${a.name}`,
    label: a.name,
    localName: a.name,
    dataType: a.dataType,
    // Unlike globals, a space-local select attribute does carry its options.
    values: a.options ?? null,
  }));
  return {
    showSpace: false,
    // Alias-resolved: the attribute key is environment data, not a contract.
    hasStatus: spaceHasStatusColumn(spaceObj, globalLib),
    globalCols: spaceScopeGlobalColumns(spaceObj, globalLib),
    localCols,
  };
}

/* ---------------- Row builders ---------------- */
function makeDocToRow(spaces: Space[], domains: VaultDomain[], scope: Scope) {
  return (d: VaultDocument): Row => {
    const sp = spaces.find((s) => s.id === d.space) ?? null;
    const dm = domains.find((x) => x.id === d.domain) ?? null;
    const row: Row = {
      _id: d.id,
      _file: d.file,
      _domId: d.domain,
      _review: false,
      _problem: null,
      _ts: Date.parse(d.modified) || 0,
      name: d.name,
      space: sp ? sp.name : null,
      domain: dm ? dm.name : d.domain,
      status: d.status,
      modified: d.modified,
      // Tri-state (DV-504); undefined for mocks that never saw the backend
      // mapper, which keeps the legacy every-blank-is-"—" behavior.
      _attrStates: rowAttrStates(d.attrStates, {
        globalKeys: scope.globalCols.map((c) => c.key),
        locals: scope.localCols,
      }),
    };
    scope.globalCols.forEach((c) => {
      row[c.key] = d.attrs[c.key] ?? null;
    });
    scope.localCols.forEach((c) => {
      row[c.key] = (c.localName && d.local[c.localName]) || null;
    });
    return row;
  };
}

function makeReviewToRow(spaces: Space[], domains: VaultDomain[]) {
  return (q: QuarantineItem): Row => {
    const dot = q.name.lastIndexOf(".");
    const base = dot > 0 ? q.name.slice(0, dot) : q.name;
    const ext = dot > 0 ? q.name.slice(dot + 1).toLowerCase() : q.file || "doc";
    const sp = q.space ? (spaces.find((s) => s.id === q.space) ?? null) : null;
    const dm = sp ? (domains.find((x) => x.id === sp.domain) ?? null) : null;
    return {
      _id: q.id,
      _file: ext,
      _domId: sp ? sp.domain : null,
      _review: true,
      _problem: q.problem,
      _ts: Date.parse(q.arrived) || 0,
      name: base,
      space: sp ? sp.name : null,
      domain: dm ? dm.name : null,
      status: NEEDS_REVIEW,
      modified: q.arrived,
    };
  };
}

/* ---------------- Columns ---------------- */
function buildColumns(scope: Scope, selectable: boolean): AgGrid.ColDef<Row>[] {
  const cols: AgGrid.ColDef<Row>[] = [
    {
      headerName: "Document name",
      field: "name",
      pinned: "left",
      flex: 2,
      minWidth: 320,
      filter: "agTextColumnFilter",
      checkboxSelection: selectable ? (p) => !p.data?._review : false,
      headerCheckboxSelection: selectable,
      headerCheckboxSelectionFilteredOnly: true,
      cellRenderer: NameCell,
    },
  ];

  if (scope.showSpace) {
    cols.push({
      headerName: "Space",
      field: "space",
      minWidth: 180,
      filter: "agSetColumnFilter",
      valueFormatter: dashFormatter,
    });
    cols.push({
      headerName: "Domain",
      field: "domain",
      minWidth: 150,
      filter: "agSetColumnFilter",
      cellRenderer: DomainCell,
    });
  }

  scope.globalCols.forEach((c) => {
    cols.push({
      headerName: c.label,
      field: c.key,
      minWidth: 150,
      // The control follows the attribute's data type, not a blanket set filter.
      ...attributeFilterDef(c.dataType, { mode: "client", values: c.values }),
      // Tri-state (DV-504): "—" bound-but-empty, "N/A" not bound to the
      // document's space, with a tooltip explaining either blank.
      valueFormatter: attrCellFormatter,
      tooltipValueGetter: attrCellTooltip,
      // Matches the server grid: non-facet attributes ride along hidden so they
      // stay filterable without crowding the All-spaces view.
      hide: c.hide ?? false,
    });
  });

  scope.localCols.forEach((c) => {
    cols.push({
      headerName: c.label,
      field: c.key,
      minWidth: 150,
      ...attributeFilterDef(c.dataType, { mode: "client", values: c.values }),
      valueFormatter: attrCellFormatter,
      tooltipValueGetter: attrCellTooltip,
      cellStyle: T2_CELL_STYLE,
    });
  });

  if (scope.hasStatus) {
    cols.push({
      headerName: "Status",
      field: "status",
      pinned: "right",
      minWidth: 130,
      filter: "agSetColumnFilter",
      cellRenderer: StatusCell,
    });
  }

  cols.push({
    headerName: "Last modified",
    field: "modified",
    pinned: "right",
    minWidth: 150,
    filter: false,
    sort: "desc",
    comparator: (_a, _b, na, nb) => (na.data?._ts ?? 0) - (nb.data?._ts ?? 0),
    cellRenderer: ModifiedCell,
  });

  return cols;
}

const DEFAULT_COL_DEF: AgGrid.ColDef<Row> = {
  sortable: true,
  resizable: true,
  filter: "agSetColumnFilter",
  minWidth: 120,
  flex: 1,
};

type BulkHandlers = { onCreateCollection?: (ids: string[]) => void };

/** Custom grid tool panel: acts on the current row selection (create a collection / clear). */
function BulkActionsPanel(props: {
  api: AgGrid.GridApi<Row>;
  getHandlers: () => BulkHandlers;
}) {
  const { api } = props;
  const [count, setCount] = useState(0);
  React.useEffect(() => {
    const onSel = () => setCount(api.getSelectedRows().length);
    api.addEventListener("selectionChanged", onSel);
    onSel();
    return () => {
      try {
        api.removeEventListener("selectionChanged", onSel);
      } catch {
        /* grid destroyed */
      }
    };
  }, [api]);
  return (
    <div className={styles.bulkPanel}>
      {count === 0 ? (
        <>
          <div className={styles.bulkTitle}>No documents selected</div>
          <div className={styles.bulkHint}>
            Select rows with the checkboxes in the Document name column to act on them here.
          </div>
        </>
      ) : (
        <>
          <div className={styles.bulkTitle}>
            {count} document{count === 1 ? "" : "s"} selected
          </div>
          <div className={styles.bulkActions}>
            <Button
              variant="primary"
              isFullWidth
              onClick={() =>
                props
                  .getHandlers()
                  .onCreateCollection?.(api.getSelectedRows().map((r) => r._id))
              }
            >
              Create a collection
            </Button>
            <Button variant="neutral" isFullWidth onClick={() => api.deselectAll()}>
              Clear selection
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

const STATUS_BAR = {
  statusPanels: [
    { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" as const },
    { statusPanel: "agSelectedRowCountComponent", align: "right" as const },
    { statusPanel: "agAggregationComponent", align: "right" as const },
  ],
};

/* ---------------- Component ---------------- */
export function DocumentBrowser({
  documents,
  quarantine = [],
  spaces,
  domains,
  globalLib,
  space = null,
  search = "",
  selectable = true,
  tab,
  onTabChange,
  onOpenDoc,
  onOpenReview,
  onCreateCollection,
  serverGrid,
  awaitingCount,
}: DocumentBrowserProps) {
  const [innerTab, setInnerTab] = useState<DocumentBrowserTab>("filed");
  const activeTab = tab ?? innerTab;
  const setTab = (t: DocumentBrowserTab) => (onTabChange ? onTabChange(t) : setInnerTab(t));
  const handlersRef = useRef<BulkHandlers>({ onCreateCollection });
  handlersRef.current = { onCreateCollection };

  const spaceObj = useMemo(
    () => (space ? (spaces.find((s) => s.id === space) ?? null) : null),
    [space, spaces],
  );
  const scope = useMemo(() => resolveScope(spaceObj, globalLib), [spaceObj, globalLib]);

  const scopedDocs = useMemo(
    () => (spaceObj ? documents.filter((d) => d.space === spaceObj.id) : documents),
    [documents, spaceObj],
  );
  const scopedReview = useMemo(
    () => (spaceObj ? quarantine.filter((q) => q.space === spaceObj.id) : quarantine),
    [quarantine, spaceObj],
  );
  /**
   * Count on the "Documents awaiting filing" tab. The client-side grid counts the
   * quarantine items it renders. A server-backed owner reports the count for its
   * quarantined query instead — and when it has none to report (that tab isn't
   * open, so nothing has queried for it), the badge is omitted rather than
   * claiming the `quarantine` prop's unrelated length.
   */
  const reviewCount = awaitingCount ?? (serverGrid ? undefined : scopedReview.length);

  const rows = useMemo(() => {
    const docToRow = makeDocToRow(spaces, domains, scope);
    const reviewToRow = makeReviewToRow(spaces, domains);
    if (activeTab === "awaiting") return scopedReview.map(reviewToRow);
    if (activeTab === "all")
      return [...scopedDocs.map(docToRow), ...scopedReview.map(reviewToRow)];
    return scopedDocs.map(docToRow);
  }, [activeTab, scopedDocs, scopedReview, scope, spaces, domains]);

  const columnDefs = useMemo(() => buildColumns(scope, selectable), [scope, selectable]);
  const scopeKey = spaceObj ? spaceObj.id : "all";

  const sideBar = useMemo<AgGrid.SideBarDef>(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
        ...(selectable
          ? [
              {
                id: "bulk",
                labelDefault: "Bulk actions",
                labelKey: "bulk",
                iconKey: "menu",
                toolPanel: BulkActionsPanel,
                minWidth: 260,
                maxWidth: 320,
                width: 280,
                toolPanelParams: { getHandlers: () => handlersRef.current },
              } as AgGrid.ToolPanelDef,
            ]
          : []),
      ],
    }),
    [selectable],
  );

  const healthTabsList: { key: DocumentBrowserTab; label: string; count?: number }[] = [
    { key: "filed", label: "Filed documents" },
    { key: "awaiting", label: "Documents awaiting filing", count: reviewCount },
    { key: "all", label: "All documents" },
  ];

  return (
    <div>
      <div className={styles.browseActions}>
        <div className={styles.healthTabs} role="tablist">
          {healthTabsList.map((t) => {
            const on = activeTab === t.key;
            return (
              <button
                key={t.key}
                role="tab"
                type="button"
                aria-selected={on}
                className={on ? styles.healthTab.on : styles.healthTab.off}
                onClick={() => setTab(t.key)}
              >
                {t.label}
                {t.count != null && (
                  <span
                    className={
                      on
                        ? styles.svCount.active
                        : t.count
                          ? styles.svCount.warn
                          : styles.svCount.neutral
                    }
                  >
                    {t.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* When provided, the server-backed slot drives every tab — the owner maps
          the active tab onto the query's `validationStatus`. The slot brings its
          own results/gridWrap layout and empty state. */}
      {serverGrid ? (
        serverGrid
      ) : (
        <div className={styles.results}>
          {rows.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <Icon type="search" size="xx-large" />
              </div>
              <div className={styles.emptyTitle}>No documents in this view</div>
              <div className={styles.emptyText}>
                {activeTab === "awaiting"
                  ? "Nothing is awaiting filing here."
                  : "Try a different space or filter."}
              </div>
            </div>
          ) : (
            <div className={styles.gridWrap}>
              <Grid<Row>
                key={scopeKey}
                rowData={rows}
                columnDefs={columnDefs}
                defaultColDef={DEFAULT_COL_DEF}
                getRowId={(p) => String(p.data._id)}
                rowHeight={48}
                headerHeight={42}
                animateRows
                rowSelection="multiple"
                suppressRowClickSelection
                isRowSelectable={(node) => !node.data?._review}
                getRowStyle={(p) => (p.data?._review ? REVIEW_ROW_STYLE : undefined)}
                sideBar={sideBar}
                statusBar={STATUS_BAR}
                quickFilterText={search}
                onRowClicked={(e) => {
                  const d = e.data;
                  if (!d) return;
                  if (d._review) onOpenReview?.(d._id);
                  else onOpenDoc?.(d._id);
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
